import { type ColumnType, Table, Text, theme } from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import type { FundAdditionalIncomeAndExpenditureModel } from '@/features/project/projects/_models/fund-additiona-income-and-expenditure';
import { useId } from '@/hooks';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  type FundIncomeAndExpenditureDataSourceType,
  convertPriorToList,
  getFundIncomeAndExpenditureDataSource,
} from '../../../_utils/fund-income-and-expenditure-table';
import { CellValue } from './CellValue';

type Props = {
  fiscalYear: number;
  data: z.infer<typeof FundAdditionalIncomeAndExpenditureModel>;
};

const PINPOINT_SHEET_TYPE = 'KS_STANDARD';
const NAME_COLUMN_WIDTH = 220;
const DATA_COLUMN_MIN_WIDTH = 140;

export const FundAdditionalIncomeAndExpenditureTable: FC<Props> = ({
  fiscalYear,
  data,
}) => {
  const { id: projectId } = useId();
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;

  const pastFundAdditionalIncomeAndExpenditureListSortedByFiscalYear =
    convertPriorToList({ value: data })
      .map((v, i) => ({ ...v, fiscalYear: fiscalYear - i - 1 }))
      .sort((a, b) => a.fiscalYear - b.fiscalYear);

  const dataSource = getFundIncomeAndExpenditureDataSource({
    fiscalYear,
    isDebugMode,
    pastFundAdditionalIncomeAndExpenditureListSortedByFiscalYear,
    data,
  });

  return (
    <PinpointMarker
      pinpoint={getPinpointSceneAndFieldBySheetType({
        pinpointSheetType: PINPOINT_SHEET_TYPE,
        ksStandard: {
          path: { project: projectId },
          field: 'ks.standard.income-and-expenditure-table',
        },
      })}
    >
      <div
        style={{
          backgroundColor: token.colorBgLayout,
        }}
      >
        <Table
          bordered
          pagination={false}
          rowKey="key"
          dataSource={dataSource}
          columns={[
            {
              title: <Text text={`(${t('thousand-yen-unit')})`} />,
              dataIndex: 'mainHeaderName',
              key: 'mainHeaderName',
              fixed: 'left',
              width: NAME_COLUMN_WIDTH,
              render: (value, record) => {
                if (
                  record.key ===
                    'fund-balance-at-the-end-of-previous-fiscal-year' ||
                  record.key === 'returned-to-the-national-treasury' ||
                  record.key === 'returned-to-the-organization' ||
                  record.key === 'fund-balance-at-the-end-of-fiscal-year' ||
                  record.key === 'national-equivalent-amount-of-fund-balance'
                ) {
                  return {
                    children: <Text weight="regular" text={value} />,
                    props: {
                      colSpan: 2,
                    },
                  };
                }
                if (record.key === 'income') {
                  return {
                    children: <Text weight="regular" text={value} />,
                    props: {
                      rowSpan: 7,
                    },
                  };
                }
                if (record.key === 'expense') {
                  return {
                    children: <Text weight="regular" text={value} />,
                    props: {
                      rowSpan: 6,
                    },
                  };
                }
                if (record.key === 'clerical-personnel-expenses') {
                  return {
                    children: <Text weight="regular" text={value} />,
                    props: {
                      rowSpan: 3,
                    },
                  };
                }
                if (value === 'hidden') {
                  return {
                    children: '',
                    props: {
                      rowSpan: 0,
                    },
                  };
                }
                return { children: <Text weight="regular" text={value} /> };
              },
            },
            {
              title: '',
              dataIndex: 'headerName',
              key: 'headerName',
              fixed: 'left',
              width: NAME_COLUMN_WIDTH,
              render: (value, record) => {
                if (
                  record.key ===
                    'fund-balance-at-the-end-of-previous-fiscal-year' ||
                  record.key === 'returned-to-the-national-treasury' ||
                  record.key === 'returned-to-the-organization' ||
                  record.key === 'fund-balance-at-the-end-of-fiscal-year' ||
                  record.key === 'national-equivalent-amount-of-fund-balance'
                ) {
                  return {
                    children: null,
                    props: {
                      colSpan: 0,
                    },
                  };
                }
                return { children: <Text weight="regular" text={value} /> };
              },
            },
            ...pastFundAdditionalIncomeAndExpenditureListSortedByFiscalYear.map<
              ColumnType<FundIncomeAndExpenditureDataSourceType>
            >((v) => {
              return {
                title: <Text text={v.fiscalYear.toString()} />,
                dataIndex: v.fiscalYear.toString(),
                key: v.fiscalYear.toString(),
                width: DATA_COLUMN_MIN_WIDTH,
                align: 'right',
                render: (value, record) => (
                  <CellValue
                    value={value}
                    dataKey={record.key}
                    targetYear={
                      v.fiscalYear === fiscalYear - 1 ? 'previousYear' : null
                    }
                    fiscalYear={fiscalYear}
                    data={data}
                  />
                ),
              };
            }),
            {
              title: <Text text={t('current-fiscal-year-estimation')} />,
              dataIndex: 'currentYear',
              key: 'currentYear',
              width: DATA_COLUMN_MIN_WIDTH,
              align: 'right',
              render: (_, record) => (
                <CellValue
                  value={record.currentYear.value}
                  dataKey={record.key}
                  targetYear="currentYear"
                  fiscalYear={fiscalYear}
                  data={data}
                />
              ),
            },
            {
              title: <Text text={t('next-fiscal-year-estimation')} />,
              dataIndex: 'nextYear',
              key: 'nextYear',
              width: DATA_COLUMN_MIN_WIDTH,
              align: 'right',
              render: (_, record) => (
                <CellValue
                  value={record.nextYear.value}
                  dataKey={record.key}
                  targetYear="nextYear"
                  fiscalYear={fiscalYear}
                  data={data}
                />
              ),
            },
          ]}
        />
      </div>
    </PinpointMarker>
  );
};
